/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  ConfirmDialog,
  WbCard,
  WbDockableGroup,
  WbDockablePanel,
  WbDockableResizeHandle,
  WbCardActions,
  WbButton,
} from '@agilelab/plugin-wb-platform';
import {
  errorApiRef,
  useAnalytics,
  useApi,
  useRouteRefParams,
} from '@mainstage/core-plugin-api';
import { JsonObject } from '@mainstage/types';
import { Box, makeStyles, Typography } from '@material-ui/core';
import { FormProps, IChangeEvent, withTheme } from '@rjsf/core';
import { Theme as MuiTheme } from '@rjsf/material-ui';
import {
  Fragment,
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { selectedTemplateRouteRef } from '../../routes';
import { ScaffolderThemeProvider } from '../ScaffolderThemeProvider/ScaffolderThemeProvider';
import { StepperElement } from '../StepperElement/StepperElement';
import { StepperPreview } from '../StepperElement/StepperPreview';
import { DefaultFieldTemplate } from '../fields';
import { DefaultArrayFieldTemplate } from '../fields/ArrayFieldTemplate/DefaultArrayFieldTemplate';
import { DefaultObjectFieldTemplate } from '../fields/ArrayFieldTemplate/DefaultObjectFieldTemplate';
import * as widgetOverrides from './WidgetOverrides';
import * as templateOverrides from './TemplateOverrides';
import { transformErrors } from './errorsOverrides';
import { AdditionalStep, collectPropertiesFromSchema, Step } from './utils';
import { WittyFormContextProvider } from '@agilelab/plugin-wb-witty-react';
import { FormExtraConfigContextProvider } from '../../contexts/FormExtraConfigContext';
import { FieldExtensionOptions, WmUiSchema } from '../../extensions';
import { ProcessingDialog } from './Modals/ProcessingDialog';
import { useDefaultFields } from './useDefaultFields';
import { useDependencyManagedForm } from './useDependencyManagedForm';

const useStyles = makeStyles(() => ({
  form: { paddingRight: '12px', paddingLeft: '12px', overflow: 'hidden' },
  box: { minWidth: '500px', width: '100%' },
}));
const Form = withTheme(MuiTheme);
type Props = {
  /**
   * Steps for the form, each contains title and form schema
   */
  steps: Step[];
  formData: Record<string, any>;
  headerActions?: React.ReactNode;
  initialStep?: number;
  onChange: (e: IChangeEvent) => void;
  widgets?: FormProps<any>['widgets'];
  fields?: FieldExtensionOptions<any, any>[];
  additionalSteps?: AdditionalStep[];
  rootStepperStyle?: JsonObject;
  stepContentStyle?: JsonObject;
  viewStepperLabel?: boolean;
  customBackButton?: JSX.Element;
  customNextButton?: JSX.Element;
  customOnSubmitFn?: () => Promise<void>;
  previewStep?: number;
};

export function getUiSchemasFromSteps(steps: Step[]): WmUiSchema[] {
  const uiSchemas: Array<WmUiSchema> = [];
  steps.forEach(step => {
    const schemaProps: JsonObject = {};
    collectPropertiesFromSchema(step.schema as JsonObject, schemaProps);
    for (const key in schemaProps) {
      if (schemaProps.hasOwnProperty(key)) {
        const uiSchema =
          schemaProps[key] && typeof schemaProps[key] === 'object'
            ? (schemaProps[key] as WmUiSchema)
            : {};
        uiSchema.name = key;
        uiSchemas.push(uiSchema);
      }
    }
  });
  return uiSchemas;
}

export const MultiStepJsonEditForm = (props: Props) => {
  const {
    formData,
    onChange,
    fields,
    widgets,
    steps,
    additionalSteps,
    rootStepperStyle,
    customOnSubmitFn,
    initialStep,
    previewStep,
    headerActions,
  } = props;
  const { templateName } = useRouteRefParams(selectedTemplateRouteRef);
  const ref: MutableRefObject<any> = useRef(null);
  const submitRef = useRef<any>(null);
  const analytics = useAnalytics();
  const [activeStep, setActiveStep] = useState(initialStep ?? 0);
  const [openDialog, setOpenDialog] = useState(false);
  const errorApi = useApi(errorApiRef);
  const toolbarHeight = useRef(64);

  const usedFields = useDefaultFields(fields);

  // Setup toolbar height
  useEffect(() => {
    const witboostToolbar = document.getElementById('witboost-toolbar');
    if (witboostToolbar) {
      toolbarHeight.current = witboostToolbar.clientHeight;
    }
  });

  // hardcoded for EditorWizardPage, shows the processing modal when true
  // TODO remove hardcoded behaviour for EditorWizardPage, should be handled from there
  const [showProcessing, setShowProcessing] = useState(false);

  // this is the step we want to go to but we must first submit the form and check that validation is succesful
  const desiredStepRef = useRef(0);

  // wether the current active step is a form step, false if current step is an additional step
  const formStepActive = activeStep < steps.length;

  // we currently have specific behaviour for EditorWizardPage and we determine it based on the number of additionalSteps
  // TODO remove hardcoded behaviour for EditorWizardPage, should be handled from there
  const isEditorWizardPage = additionalSteps?.length === 3;

  // hardcoded for EditorWizardPage, where the processing step is always present and is the second last step
  // TODO remove hardcoded behaviour for EditorWizardPage, should be handled from there
  const processingStepIndex = steps.length + (additionalSteps?.length ?? 0) - 2;

  const [error, setError] = useState<any>(null);

  // Clear extra errors when the step changes to prevent stale errors from persisting
  useEffect(() => {
    setError(null);
  }, [activeStep]);

  const { currentFormStep, currentAdditionalStepIndex } = useMemo(() => {
    if (formStepActive) {
      return {
        currentFormStep: steps[activeStep],
        currentAdditionalStepIndex: null,
      };
    }
    return {
      currentFormStep: null,
      currentAdditionalStepIndex: activeStep - steps.length,
    };
  }, [activeStep, steps, formStepActive]);

  // Get current form step for dependency manager
  const currentStep = formStepActive ? steps[activeStep] : null;

  // Use the centralized hook for dependency manager integration
  const { dependencyManager, handleFormChange } = useDependencyManagedForm({
    formData,
    onChange,
    currentStep: currentStep,
  });

  const handleNext = (newStep: number) => {
    const step = newStep;

    if (step === processingStepIndex && additionalSteps?.[1]) {
      setShowProcessing(true);
    }

    if (showProcessing) setShowProcessing(false);
    setActiveStep(step);
    analytics.captureEvent('click', `Next Step (${step})`);
  };

  const {
    title: additionalTitle,
    component,
    additionalActions,
    backButtonText,
    nextButtonText,
    disableStepButtons,
    customDisableStepButtons,
    resetButtonText,
    analyticsEvent,
    onSaveButtonClick,
    nextAction,
  } = additionalSteps?.[currentAdditionalStepIndex ?? -1] ?? {};

  // hardcoded for EditorWizardPage, where the processing is provided as nextAction of the first additional step, Review and Information
  // TODO remove hardcoded behaviour for EditorWizardPage, should be handled from there
  const startProcessing = () => {
    if (additionalSteps?.[0].nextAction) {
      additionalSteps?.[0].nextAction();
    }
  };

  const styles = useStyles();

  const handleStep = (e: number) => {
    desiredStepRef.current = e;
    if (currentFormStep) {
      submitRef?.current.click();
    } else if (e === processingStepIndex) {
      // hardcoded for starting process when entering processingStep of EditorWizardPage
      // TODO remove hardcoded behaviour for EditorWizardPage, should be handled from there
      startProcessing();
      if (analyticsEvent) {
        analytics.captureEvent(
          analyticsEvent,
          formData.name || `new ${templateName}`,
        );
      }
      handleNext(e);
    } else setActiveStep(e);
  };

  const title = currentFormStep?.title ?? additionalTitle ?? '';

  const lastAdditionalStepActive =
    activeStep === steps.length + (additionalSteps?.length ?? 0) - 1;

  // in EditorWizardPage, comparison step should be rendered outside WbDockableGroup, in its own single panel
  // TODO remove hardcoded behaviour for EditorWizardPage, should be handled from there
  const renderComparisonStep = lastAdditionalStepActive && isEditorWizardPage;

  const currentStepperStep =
    currentFormStep ||
    additionalSteps?.[currentAdditionalStepIndex ?? -1] ||
    null;

  // the additional step content rendered as jsx
  const additionalStepJsx = (
    <Box
      style={{
        width:
          additionalTitle ===
          additionalSteps?.[additionalSteps?.length - 1].title
            ? '100%'
            : 'auto',
      }}
    >
      {activeStep === steps.length && (
        <Typography color="textPrimary" style={{ padding: '8px' }} variant="h3">
          {additionalTitle}
        </Typography>
      )}
      {component}
    </Box>
  );

  return (
    <WbCard
      title="Edit template wizard"
      cardStyle={{ height: '100%' }}
      actions={
        <WbCardActions breakpoint="w_xs">
          {activeStep <= steps.length && (
            <>
              <WbButton
                form={`form-${title}`}
                variant="outlined"
                color="primary"
                disabled={activeStep === 0}
                onClick={() => {
                  handleStep(activeStep - 1);
                }}
                label="Back"
              />
              <WbButton
                form={`form-${title}`}
                variant="outlined"
                color="primary"
                disabled={activeStep === steps.length}
                onClick={() => handleStep(activeStep + 1)}
                label="Next"
              />
            </>
          )}
          {/* this is hardcoded back button for last additional step of EditorWizardPage */}
          {/* TODO remove hardcoded behaviour for EditorWizardPage, should be handled from there */}
          {lastAdditionalStepActive && isEditorWizardPage && (
            <WbButton
              form={`form-${title}`}
              variant="outlined"
              color="primary"
              disabled={activeStep === 0}
              onClick={() => setOpenDialog(true)}
              label="Back"
            />
          )}
          {additionalActions?.map(action => (
            <Fragment key={action.key}>{action}</Fragment>
          ))}
          {((formStepActive && previewStep) || onSaveButtonClick) && (
            <WbButton
              variant="contained"
              color="primary"
              form={`form-${title}`}
              onClick={() => {
                if (onSaveButtonClick) {
                  onSaveButtonClick({
                    setStep: handleStep,
                    setAdditionalStep: additionalStep =>
                      handleStep(steps.length + additionalStep),
                  });
                  return;
                }
                if (previewStep) handleStep(previewStep);
              }}
              disabled={
                customDisableStepButtons?.disableNext ?? disableStepButtons
              }
              label={onSaveButtonClick ? 'Save' : 'Preview and Save'}
            />
          )}
          {headerActions}
        </WbCardActions>
      }
    >
      <ScaffolderThemeProvider>
        {!renderComparisonStep && (
          <WbDockableGroup direction="horizontal">
            {/* show sections panel and resize handle only for first additionalStep, since <StepperElement /> is
          hardcoded to only show first as section */}
            {/* TODO this should not be hardcoded, should be handled outside */}
            {activeStep <= steps.length && (
              <WbDockablePanel
                showCollapsedContent
                isCardChildren
                collapsible
                title="Sections"
                maxSize={40}
                minSize={15}
                defaultSize={30}
                preview={
                  <StepperPreview
                    steps={steps}
                    currentStep={currentStepperStep}
                    activeStep={activeStep}
                    ref={ref}
                    setActiveStep={handleStep}
                    additionalSteps={additionalSteps}
                    rootStepperStyle={rootStepperStyle}
                  />
                }
              >
                <StepperElement
                  steps={steps}
                  currentStep={currentStepperStep}
                  activeStep={activeStep}
                  ref={ref}
                  setActiveStep={handleStep}
                  additionalSteps={additionalSteps}
                  rootStepperStyle={rootStepperStyle}
                />
              </WbDockablePanel>
            )}
            {activeStep <= steps.length && (
              <WbDockableResizeHandle direction="horizontal" />
            )}
            <WbDockablePanel>
              {/* shown when currentFormStep defined, currentFormStep means it is a step from the form rather than an additionalStep */}
              {currentFormStep && (
                <>
                  <Typography
                    color="textPrimary"
                    style={{ padding: '8px' }}
                    variant="h3"
                  >
                    {currentFormStep.title}
                  </Typography>
                  <FormExtraConfigContextProvider debounceMs={300}>
                    <WittyFormContextProvider
                      onChange={onChange}
                      formData={formData}
                      formDefinition={currentFormStep?.schema}
                    >
                      <Form
                        key={activeStep}
                        id={`form-${currentFormStep.title ?? additionalTitle}`}
                        extraErrors={error}
                        className={styles.form}
                        templates={{
                          ArrayFieldTemplate: DefaultArrayFieldTemplate,
                          ObjectFieldTemplate: DefaultObjectFieldTemplate,
                          FieldTemplate: DefaultFieldTemplate,
                          ...templateOverrides,
                        }}
                        fields={usedFields}
                        widgets={{ ...widgetOverrides, ...widgets }}
                        noHtml5Validate
                        formData={formData}
                        onChange={handleFormChange}
                        showErrorList="top"
                        noValidate={currentFormStep?.noValidate}
                        onSubmit={async e => {
                          const desiredStep = desiredStepRef.current;
                          if (currentFormStep?.asyncValidate) {
                            const validationResult =
                              await currentFormStep?.asyncValidate(formData);
                            if (validationResult) {
                              const keysWithErrors = Object.keys(
                                validationResult,
                              ).filter(
                                key => validationResult[key]?.__errors?.length,
                              );

                              if (
                                keysWithErrors.length === 0 &&
                                e.errors.length === 0
                              ) {
                                if (typeof customOnSubmitFn === 'function') {
                                  await customOnSubmitFn();
                                }
                                handleNext(desiredStep);
                              } else {
                                setError(validationResult);
                              }
                            } else {
                              if (e.errors.length === 0) {
                                if (typeof customOnSubmitFn === 'function') {
                                  await customOnSubmitFn();
                                }
                                handleNext(desiredStep);
                              }
                            }
                          } else {
                            if (e.errors.length === 0) {
                              if (typeof customOnSubmitFn === 'function') {
                                await customOnSubmitFn();
                              }
                              handleNext(desiredStep);
                            }
                          }
                          if (desiredStep === processingStepIndex) {
                            try {
                              if (additionalSteps?.[0].nextAction)
                                await additionalSteps?.[0].nextAction();
                              if (analyticsEvent) {
                                analytics.captureEvent(
                                  analyticsEvent,
                                  formData.name || `new ${templateName}`,
                                );
                              }
                              handleNext(desiredStep);
                            } catch (err) {
                              errorApi.post(err);
                            }
                          }
                        }}
                        {...currentFormStep}
                        formContext={{
                          ...formData,
                          // Add dependency manager to context for pickers
                          dependencyManager,
                          // Add root schema to context for field path extraction
                          rootSchema: currentFormStep?.schema,
                        }}
                        transformErrors={transformErrors}
                      >
                        <button
                          form={`form-${title}`}
                          color="primary"
                          style={{ display: 'none' }}
                          ref={submitRef}
                          type="submit"
                        />
                      </Form>
                    </WittyFormContextProvider>
                  </FormExtraConfigContextProvider>
                </>
              )}
              {/* if an additional Step is defined BUT must not be processing, it will be shown here */}
              {/* TODO remove processingStep hardcoded logic, should be handled outside */}
              {currentAdditionalStepIndex !== null &&
                activeStep !== processingStepIndex &&
                additionalStepJsx}
            </WbDockablePanel>
          </WbDockableGroup>
        )}
        {/* Comparison step in EditorWizardPage is rendered outside WbDockableGroup */}
        {renderComparisonStep && additionalStepJsx}
      </ScaffolderThemeProvider>

      {/* processing step modal for EditorWizardPage */}
      {/* TODO remove hardcoded behaviour for EditorWizardPage, should be handled from there */}
      <ProcessingDialog
        open={showProcessing}
        onClose={() => setShowProcessing(false)}
        onBack={() => {
          if (previewStep) setActiveStep(previewStep);
        }}
        isError={additionalSteps?.[1]?.taskFailed}
        isSuccess={
          !additionalSteps?.[1]?.taskFailed &&
          !additionalSteps?.[1]?.disableStepButtons
        }
        submitButton={
          <WbButton
            variant="contained"
            color="primary"
            disabled={
              additionalSteps?.[1]?.customDisableStepButtons?.disableNext ??
              additionalSteps?.[1]?.disableStepButtons
            }
            type="submit"
            label={nextButtonText}
            onClick={async () => {
              try {
                if (nextAction) await nextAction();
                if (analyticsEvent) {
                  analytics.captureEvent(
                    analyticsEvent,
                    formData.name || `new ${templateName}`,
                  );
                }
                setShowProcessing(false);
                setActiveStep(
                  steps?.length + (additionalSteps?.length ?? 0) - 1,
                );
              } catch (err) {
                errorApi.post(err);
              }
            }}
          />
        }
      >
        <Box style={{ minWidth: '100%', height: '60vh' }}>
          {additionalSteps?.[1]?.component}
          {(resetButtonText || nextButtonText || backButtonText) && (
            <Box mb={3} />
          )}
        </Box>
      </ProcessingDialog>
      {/* TODO remove hardcoded behaviour for EditorWizardPage, should be handled from there */}
      {lastAdditionalStepActive && isEditorWizardPage ? (
        <>
          <ConfirmDialog
            title="Go back to the Wizard"
            description="Are you sure you want to go back and discard any modifications made on this page?"
            open={openDialog}
            onClose={() => {
              setOpenDialog(false);
            }}
            onConfirm={() => {
              setOpenDialog(false);
              setActiveStep(steps.length);
            }}
            confirmButtonText="Yes, go back"
          />
        </>
      ) : (
        <span />
      )}
    </WbCard>
  );
};
